import * as React from "react";
import { dragManager } from "./DragManager";
import { DropPosition, DropZone } from "./types";

interface UseDroppableOptions {
  position: DropPosition;
  disabled?: boolean;
}

interface UseDroppableReturn {
  ref: React.RefCallback<HTMLElement>;
  isOver: boolean;
}

let dropZoneIdCounter = 0;

export function useDroppable(options: UseDroppableOptions): UseDroppableReturn {
  const { position, disabled = false } = options;
  // The element is kept in state so that the zone is re-registered when it is
  // remounted. The position is copied onto the registered zone after every
  // render instead of re-registering for each new position object.
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [isOver, setIsOver] = React.useState(false);
  const zoneIdRef = React.useRef<string>(`dropzone-${++dropZoneIdCounter}`);
  const positionRef = React.useRef(position);
  const zoneRef = React.useRef<DropZone | null>(null);

  React.useEffect(() => {
    positionRef.current = position;
    if (zoneRef.current) {
      zoneRef.current.type = position.type;
      zoneRef.current.payload = position;
    }
  });

  React.useEffect(() => {
    if (!element || disabled) {
      return;
    }

    const zoneId = zoneIdRef.current;
    const zone: DropZone = {
      id: zoneId,
      type: positionRef.current.type,
      element,
      payload: positionRef.current,
    };
    zoneRef.current = zone;

    const unsubscribeMove = dragManager.onDragMove((x, y, activeZone) => {
      setIsOver(activeZone?.id === zoneId);
    });

    const unsubscribeEnd = dragManager.onDragEnd(() => {
      setIsOver(false);
    });

    const unregister = dragManager.registerDropZone(zone);

    return () => {
      unregister();
      unsubscribeMove();
      unsubscribeEnd();
      zoneRef.current = null;
      setIsOver(false);
    };
  }, [element, disabled]);

  return { ref: setElement, isOver };
}
